import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CallBar,
  ConversationList,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Icons,
  iconSize,
  InputAdornment,
  MenuItem,
  MessageComposer,
  MessageThread,
  palette,
  Stack,
  TextField,
  ToggleChip,
  type ConversationListItem,
  type ThreadMessage,
  Typography,
  useIsDesktop,
  useSnackbar,
} from '@munifocus/ui';
import { apiErrorMessage } from '../../lib/mutation-toast';
import { createDraftKeyMinter, flattenMessagePages, presenceBucket } from '@munifocus/chat';
import type { ChatConversationView, ChatMessageView } from '@munifocus/chat';
import {
  CHAT_ATTACHMENT_ACCEPT,
  MAX_CHAT_ATTACHMENT_BYTES,
  resolveChatContentType,
  type AttachmentDto,
} from '@munifocus/contracts-common';
import {
  useConversations,
  useMarkConversationRead,
  useMessages,
  useSendMessage,
  useUploadAttachment,
} from '../../lib/chat-api';
import { useRealtime } from '../../lib/realtime';
import { useVoiceCalls } from '../../lib/use-voice-calls';
import { useFileMentions } from '../teamwork/useFileMentions';
import { useAuth } from '../../providers/AuthProvider';
import { frenchFirstOfMonth } from '@munifocus/utils';

/** A picked file, and the descriptor once its upload lands. */
interface PendingUpload {
  id: string;
  fileName: string;
  uploading: boolean;
  error?: string;
  descriptor?: AttachmentDto;
}

export interface ChatPanelProps {
  /** Opens straight into this thread — used when arriving from a file. */
  initialConversationId?: string | null;
  /** Fills the viewport (the Messages route) rather than a fixed-height drawer. */
  fullHeight?: boolean;
}

/**
 * The conversations surface: thread list beside the open transcript.
 *
 * **Why one component for the page and the drawer.** Both show the same two
 * panes; only the height differs. Splitting them would mean two places to fix
 * every behaviour below, and they would drift the first time one was touched.
 *
 * On a narrow viewport the two panes become one — list, or thread with a back
 * button. Officers work this on a phone in the field, and a 40/60 split at
 * 390px is unusable for both halves at once.
 */
export function ChatPanel({ initialConversationId, fullHeight }: ChatPanelProps) {
  const { t, i18n } = useTranslation();
  const isDesktop = useIsDesktop();
  const snackbar = useSnackbar();
  const realtime = useRealtime();
  const { user } = useAuth();
  const voice = useVoiceCalls();

  const conversations = useConversations();
  const [selectedId, setSelectedId] = useState<string | null>(initialConversationId ?? null);
  const messages = useMessages(selectedId);
  const loadedMessages = useMemo(() => flattenMessagePages(messages.data), [messages.data]);
  const sendMessage = useSendMessage(selectedId ?? '');
  const markRead = useMarkConversationRead();
  const uploadAttachment = useUploadAttachment(selectedId ?? '');

  // One idempotency key per draft, held across manual retries of it — see
  // `createDraftKeyMinter`. A ref, so it survives re-renders but not a remount.
  const draftKeys = useRef(createDraftKeyMinter(() => crypto.randomUUID())).current;

  // Files picked but not yet sent. Local state, keyed by a client-side id so a
  // row can be removed while its upload is still in flight.
  const [pending, setPending] = useState<PendingUpload[]>([]);

  // Narrowing controls. All three are client-side over what is already loaded:
  // the conversation list arrives whole, and the transcript is paginated, so a
  // search here reaches the messages on screen rather than the whole history —
  // which the empty state says out loud rather than implying a complete result.
  const [conversationQuery, setConversationQuery] = useState('');
  const [membersOpen, setMembersOpen] = useState(false);
  /**
   * Which slice of the list is shown.
   *
   * `unread` earns its place over the other three: an officer opening Messages
   * is almost always answering something, and with a standing room per
   * municipality the unread ones are a handful in a list that only grows.
   */
  const [conversationFilter, setConversationFilter] = useState<
    'all' | 'unread' | 'channel' | 'direct'
  >('all');
  const [messageQuery, setMessageQuery] = useState('');
  const [authorFilter, setAuthorFilter] = useState('all');

  // Dropped whenever the thread changes: an attachment belongs to the
  // conversation it was uploaded into, and the server refuses a key from any
  // other — so carrying it across would guarantee a rejected send.
  useEffect(() => setPending([]), [selectedId]);

  const selected = conversations.data?.conversations.find((c) => c.id === selectedId) ?? null;
  // @mentions belong to a file's discussion only: the people offered are those
  // who can read that file, which only a file thread has to ask about.
  const mentionFileId = selected?.kind === 'file' ? (selected.fileId ?? '') : '';
  const fileMentions = useFileMentions(mentionFileId, Boolean(mentionFileId));

  // Follow an id handed in after mount — opening a file's thread while the
  // drawer is already on screen should switch to it, not be ignored.
  useEffect(() => {
    if (initialConversationId) setSelectedId(initialConversationId);
  }, [initialConversationId]);

  // Subscribe to the open thread's typing/read signals for exactly as long as
  // it is on screen. The cleanup is what stops a long session accumulating
  // every room the officer has ever glanced at.
  useEffect(() => {
    if (!selectedId) return;
    return realtime.watchConversation(selectedId);
  }, [selectedId, realtime]);

  // Held in a ref because react-query hands back a new mutation object every
  // render: depending on it directly would re-run this effect continuously, and
  // suppressing that with a lint exception would just hide the same problem.
  const markReadRef = useRef(markRead.mutate);
  markReadRef.current = markRead.mutate;

  // Opening a thread with anything unread clears it — including when a message
  // arrives over the socket while it is already on screen. Guarded on the count
  // so simply having it open does not fire a write on every cache update.
  const unreadHere = selected?.unreadCount ?? 0;
  useEffect(() => {
    if (selectedId && unreadHere > 0) markReadRef.current(selectedId);
  }, [selectedId, unreadHere]);

  const locale = i18n.language.startsWith('fr') ? 'fr-CA' : 'en-CA';
  const listItems: ConversationListItem[] = useMemo(
    () =>
      (conversations.data?.conversations ?? []).map((conversation) => ({
        id: conversation.id,
        title: titleOf(conversation, t),
        preview: conversation.lastMessagePreview,
        timestamp: conversation.lastMessageAt
          ? shortStamp(conversation.lastMessageAt, locale)
          : null,
        unreadCount: conversation.unreadCount,
        kind: conversation.kind,
        muted: isMuted(conversation.mutedUntil),
      })),
    [conversations.data, locale, t],
  );

  /**
   * Counts per filter, so a tab that would show nothing says so before it is
   * pressed — and so "Unread 3" answers the question people actually open this
   * panel with.
   */
  const filterCounts = useMemo(
    () => ({
      all: listItems.length,
      unread: listItems.filter((item) => item.unreadCount > 0).length,
      channels: listItems.filter((item) => item.kind === 'channel').length,
      direct: listItems.filter((item) => item.kind === 'direct').length,
    }),
    [listItems],
  );

  const visibleConversations = useMemo(() => {
    const byFilter =
      conversationFilter === 'all'
        ? listItems
        : conversationFilter === 'unread'
          ? listItems.filter((item) => item.unreadCount > 0)
          : listItems.filter((item) => item.kind === conversationFilter);

    const needle = conversationQuery.trim().toLowerCase();
    if (!needle) return byFilter;
    // Title and last-message preview both, so searching a phrase someone used
    // finds the thread even when its name gives nothing away — which is most
    // group threads, whose names are file numbers.
    return byFilter.filter(
      (item) =>
        item.title.toLowerCase().includes(needle) ||
        (item.preview ?? '').toLowerCase().includes(needle),
    );
  }, [listItems, conversationQuery, conversationFilter]);

  const threadMessages: ThreadMessage[] = useMemo(
    () =>
      loadedMessages.map((message) => ({
        id: message.id,
        body: message.system
          ? // System lines carry an i18n key rather than text, so they render in
            // the *reader's* language rather than whoever triggered them.
            t(message.system.key, message.system.values)
          : message.call
            ? callSummary(message.call, t)
            : message.body,
        authorName: message.authorName,
        // The id, not the name: the avatar tint has to survive a rename and
        // still tell two same-named colleagues apart.
        authorId: message.author?.id ?? null,
        timestamp: fullStamp(message.createdAt, locale),
        mine: message.mine,
        deleted: Boolean(message.deletedAt),
        edited: Boolean(message.editedAt),
        // A call renders as thread chrome, not as something anyone said.
        system: message.kind === 'system' || message.kind === 'call',
        attachments: message.attachments.map((attachment) => ({
          fileName: attachment.fileName,
          url: attachment.url,
          sizeBytes: attachment.sizeBytes,
        })),
        status: message.mine ? deliveryStatus(message.createdAt, selected, user?.id) : undefined,
        mentions: message.mentions?.map((m) => ({ id: m.userId, label: m.displayName })),
      })),
    [loadedMessages, locale, t, selected, user?.id],
  );

  /**
   * Everyone who has actually said something in this thread.
   *
   * Drawn from the loaded messages rather than the participant list, so the
   * filter never offers a name that would return nothing — a member who has
   * only ever read is not a useful thing to narrow to. System and call lines
   * have no author and are skipped.
   */
  /**
   * Presence for a direct thread's correspondent, or null when the phrase has
   * no single subject (groups, channels, the file thread).
   */
  const presenceLine = useMemo(() => {
    if (selected?.kind !== 'direct') return null;
    // Until auth resolves, `user?.id` is undefined and every participant "is
    // not me" — so the find would return the first one, which may well be the
    // viewer, and briefly show them their own presence.
    if (!user?.id) return null;
    const other = selected.participants.find((p) => p.user.id !== user.id);
    if (!other) return null;

    const bucket = presenceBucket(other.user.lastSeenAt);
    switch (bucket.kind) {
      case 'never':
        return t('common.chat.presenceNever');
      case 'online':
        return t('common.chat.presenceOnline');
      case 'minutes':
        return t('common.chat.presenceMinutes', { count: bucket.minutes });
      case 'hours':
        return t('common.chat.presenceHours', { count: bucket.hours });
      case 'days':
        return t('common.chat.presenceDays', { count: bucket.days });
    }
  }, [selected, user?.id, t]);

  const authors = useMemo(() => {
    const names = new Set<string>();
    for (const message of loadedMessages) {
      if (message.kind === 'system' || message.kind === 'call') continue;
      if (message.authorName) names.add(message.authorName);
    }
    return [...names].sort((a, b) => a.localeCompare(b, locale));
  }, [loadedMessages, locale]);

  // Reset when the thread changes: an author or a search term from the previous
  // conversation would silently hide everything in the new one.
  useEffect(() => {
    setMessageQuery('');
    setAuthorFilter('all');
  }, [selectedId]);

  const filteredMessages = useMemo(() => {
    const needle = messageQuery.trim().toLowerCase();
    if (!needle && authorFilter === 'all') return threadMessages;
    return threadMessages.filter((message) => {
      // Narrowing is about what people said, so the thread's own chrome drops
      // out rather than surviving every filter as noise.
      if (message.system) return false;
      if (authorFilter !== 'all' && message.authorName !== authorFilter) return false;
      return !needle || message.body.toLowerCase().includes(needle);
    });
  }, [threadMessages, messageQuery, authorFilter]);

  const filteringMessages = messageQuery.trim().length > 0 || authorFilter !== 'all';

  /**
   * Why a file cannot be shared, or null when it is fine.
   *
   * The server checks both of these too and is the authority. Checking here as
   * well is what turns "upload failed" — which is what an officer saw the
   * instant they picked a `.gif`, a `.zip`, or a 40 MB video, with no hint why —
   * into a reason they can act on, without spending the upload first.
   */
  function rejectionReason(file: File): string | null {
    if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
      return t('common.chat.attachmentTooLarge', {
        limit: Math.round(MAX_CHAT_ATTACHMENT_BYTES / (1024 * 1024)),
      });
    }
    // The same resolver the server uses, so this never refuses something the
    // upload would have accepted — a file the browser could not identify is
    // settled by its extension there and here alike.
    if (!resolveChatContentType(file.name, file.type)) {
      return t('common.chat.attachmentTypeRefused');
    }
    return null;
  }

  /**
   * The reason the server gave, when it gave one.
   *
   * A rejected upload used to surface as one fixed "upload failed" line, which
   * is why an immediate refusal looked like a bug rather than a rule. Multer
   * aborts an oversized body with a 413 whose body carries no useful wording,
   * so that case is worded here; everything else quotes the API.
   */
  function uploadErrorMessage(error: unknown): string | null {
    const response = (error as { response?: { status?: number; data?: { message?: unknown } } })
      ?.response;
    if (!response) return null;
    if (response.status === 413) {
      return t('common.chat.attachmentTooLarge', {
        limit: Math.round(MAX_CHAT_ATTACHMENT_BYTES / (1024 * 1024)),
      });
    }
    const message = response.data?.message;
    if (typeof message === 'string' && message.length > 0) return message;
    if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
    return null;
  }

  function attachFiles(files: File[]) {
    for (const file of files) {
      const id = crypto.randomUUID();
      const refusal = rejectionReason(file);
      if (refusal) {
        setPending((prev) => [
          ...prev,
          { id, fileName: file.name, uploading: false, error: refusal },
        ]);
        continue;
      }

      setPending((prev) => [...prev, { id, fileName: file.name, uploading: true }]);
      uploadAttachment.mutate(file, {
        onSuccess: (descriptor) =>
          setPending((prev) =>
            prev.map((row) => (row.id === id ? { ...row, uploading: false, descriptor } : row)),
          ),
        // The server's own wording where it sent one — it knows things the
        // browser cannot, and a generic failure line hides them.
        onError: (error) =>
          setPending((prev) =>
            prev.map((row) =>
              row.id === id
                ? {
                    ...row,
                    uploading: false,
                    error: uploadErrorMessage(error) ?? t('common.chat.uploadFailed'),
                  }
                : row,
            ),
          ),
      });
    }
  }

  const typingNames = selectedId ? realtime.typingBy(selectedId) : [];
  const archived = selected?.status === 'archived';

  const showList = isDesktop || !selectedId;
  const showThread = isDesktop || Boolean(selectedId);

  return (
    <Stack
      direction="row"
      sx={{
        height: fullHeight ? '100%' : 520,
        border: `1px solid ${palette.border.line}`,
        borderRadius: 1,
        overflow: 'hidden',
        backgroundColor: palette.background.paper,
      }}
    >
      {showList && (
        <Box
          sx={{
            width: isDesktop ? 320 : '100%',
            flexShrink: 0,
            borderRight: isDesktop ? `1px solid ${palette.border.line}` : 'none',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Pinned above the scroller, not inside it: a search field that
              scrolls away is unreachable exactly when the list is long enough
              to need one. */}
          <Box sx={{ p: 1, borderBottom: `1px solid ${palette.border.line}` }}>
            <TextField
              fullWidth
              size="small"
              value={conversationQuery}
              onChange={(event) => setConversationQuery(event.target.value)}
              placeholder={t('common.chat.searchConversations')}
              aria-label={t('common.chat.searchConversations')}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icons.Search size={iconSize.sm} aria-hidden />
                    </InputAdornment>
                  ),
                },
              }}
            />

            {/* Under the search field, not beside it: at 320px a row of four
                chips and a text input cannot both have room, and the filter is
                the thing you reach for second. */}
            <Stack
              useFlexGap
              direction="row"
              spacing={0.5}
              sx={{ flexWrap: 'wrap', mt: 1 }}
              role="group"
              aria-label={t('common.chat.filterLabel')}
            >
              {(['all', 'unread', 'channel', 'direct'] as const).map((option) => {
                const count =
                  option === 'channel'
                    ? filterCounts.channels
                    : option === 'direct'
                      ? filterCounts.direct
                      : filterCounts[option];
                return (
                  <ToggleChip
                    key={option}
                    label={`${t(`common.chat.filters.${option}`)} ${count}`}
                    selected={conversationFilter === option}
                    // A filter that can only empty the list is not worth a tap.
                    disabled={count === 0 && conversationFilter !== option}
                    onToggle={() => setConversationFilter(option)}
                  />
                );
              })}
            </Stack>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            <ConversationList
              items={visibleConversations}
              selectedId={selectedId}
              loading={conversations.isLoading}
              onSelect={setSelectedId}
              labels={{
                empty: conversationQuery.trim()
                  ? t('common.chat.noConversationMatches')
                  : t('common.chat.noConversations'),
                loading: t('common.chat.loadingConversations'),
                unread: (count) => t('common.chat.unreadCount', { count }),
              }}
            />
          </Box>
        </Box>
      )}

      {showThread && (
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <>
              <Stack
                direction="row"
                sx={{
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderBottom: `1px solid ${palette.border.line}`,
                }}
              >
                {!isDesktop && (
                  <IconButton
                    size="small"
                    onClick={() => setSelectedId(null)}
                    aria-label={t('common.chat.backToList')}
                  >
                    <Icons.ArrowLeft size={iconSize.lg} aria-hidden />
                  </IconButton>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle1" noWrap>
                    {titleOf(selected, t)}
                  </Typography>
                  {/* A direct thread has one correspondent, so presence is a
                      more useful second line than "2 members". A group has no
                      single subject for the phrase, so it keeps the count —
                      and the count opens the list, because "9 members" of a
                      municipal room is a fact you can only act on if you can
                      see who they are. */}
                  {presenceLine ? (
                    <Typography variant="caption" sx={{ color: palette.text.muted }}>
                      {presenceLine}
                    </Typography>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setMembersOpen(true)}
                      sx={{
                        appearance: 'none',
                        background: 'none',
                        border: 'none',
                        p: 0,
                        font: 'inherit',
                        fontSize: 12,
                        color: palette.text.muted,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        '&:hover': { color: palette.brand.accent },
                      }}
                    >
                      {t('common.chat.memberCount', {
                        count: selected.participants.filter((p) => !p.leftAt).length,
                      })}
                    </Box>
                  )}
                </Box>

                {/* Only on a direct thread, and only when telephony is
                    configured. Twilio connects two parties; a group call needs a
                    conference, and a button that silently rang one arbitrary
                    member would be worse than no button. */}
                {voice.enabled && selected.kind === 'direct' && voice.phase === 'idle' && (
                  <IconButton
                    onClick={() => voice.callConversation(selected.id, titleOf(selected, t))}
                    aria-label={t('common.chat.startCall')}
                    sx={{ color: palette.brand.accent }}
                  >
                    <Icons.Phone size={iconSize.lg} aria-hidden />
                  </IconButton>
                )}
              </Stack>

              {/* Search and sender narrowing over the transcript. Sits under the
                  header rather than in it so the thread's identity and the
                  controls acting on it do not compete on one row at 390px. */}
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                sx={{
                  gap: 1,
                  px: 2,
                  py: 1,
                  borderBottom: `1px solid ${palette.border.line}`,
                  alignItems: { sm: 'center' },
                }}
              >
                <TextField
                  size="small"
                  value={messageQuery}
                  onChange={(event) => setMessageQuery(event.target.value)}
                  placeholder={t('common.chat.searchMessages')}
                  aria-label={t('common.chat.searchMessages')}
                  sx={{ flex: 1, minWidth: 0 }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icons.Search size={iconSize.sm} aria-hidden />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                {/* Only worth offering once more than one person has spoken:
                    below that the single option is the whole thread. */}
                {authors.length > 1 && (
                  <TextField
                    select
                    size="small"
                    value={authorFilter}
                    onChange={(event) => setAuthorFilter(event.target.value)}
                    label={t('common.chat.filterByAuthor')}
                    sx={{ minWidth: { sm: 180 } }}
                  >
                    <MenuItem value="all">{t('common.chat.allAuthors')}</MenuItem>
                    {authors.map((name) => (
                      <MenuItem key={name} value={name}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              </Stack>

              {/* Older history is fetched on request rather than all at once: the
                  first page is the latest fifty, and a long thread used to
                  stop there with nothing to reach what came before. */}
              {messages.hasNextPage && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: 0.5,
                    borderBottom: `1px solid ${palette.border.line}`,
                  }}
                >
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => void messages.fetchNextPage()}
                    disabled={messages.isFetchingNextPage}
                    aria-busy={messages.isFetchingNextPage}
                    startIcon={<Icons.ChevronUp size={iconSize.sm} aria-hidden />}
                  >
                    {messages.isFetchingNextPage
                      ? t('common.chat.loadingEarlier')
                      : t('common.chat.loadEarlier')}
                  </Button>
                </Box>
              )}

              <MessageThread
                messages={filteredMessages}
                loading={messages.isLoading}
                // Suppressed while narrowing: "someone is typing" under a
                // filtered result claims a message is about to appear there,
                // and it very likely will not match.
                typingNames={filteringMessages ? [] : typingNames}
                labels={{
                  empty: filteringMessages
                    ? t('common.chat.noMessageMatches')
                    : t('common.chat.noMessages'),
                  loading: t('common.chat.loadingMessages'),
                  deleted: t('common.chat.messageWithdrawn'),
                  edited: t('common.chat.edited'),
                  typing: (names) =>
                    names.length === 1
                      ? t('common.chat.typingOne', { name: names[0] })
                      : t('common.chat.typingMany', { count: names.length }),
                  showMore: (count) => t('common.chat.showMore', { count }),
                  showLess: t('common.chat.showLess'),
                  sent: t('common.chat.deliverySent'),
                  read: t('common.chat.deliveryRead'),
                }}
              />

              {voice.phase !== 'idle' && (
                <CallBar
                  phase={voice.phase}
                  peerName={voice.peerName}
                  elapsedSeconds={voice.elapsedSeconds}
                  muted={voice.muted}
                  onAccept={voice.accept}
                  onDecline={voice.decline}
                  onHangUp={voice.hangUp}
                  onToggleMute={voice.toggleMute}
                  labels={{
                    incoming: t('common.chat.call.incoming'),
                    calling: t('common.chat.call.calling'),
                    unknownCaller: t('common.chat.call.unknownCaller'),
                    accept: t('common.chat.call.accept'),
                    decline: t('common.chat.call.decline'),
                    hangUp: t('common.chat.call.hangUp'),
                    mute: t('common.chat.call.mute'),
                    unmute: t('common.chat.call.unmute'),
                  }}
                />
              )}

              <MessageComposer
                disabled={archived || sendMessage.isPending}
                disabledReason={archived ? t('common.chat.archivedNotice') : undefined}
                onTypingChange={(typing) => realtime.setTyping(selected.id, typing)}
                attachments={pending.map(({ id, fileName, uploading, error }) => ({
                  id,
                  fileName,
                  uploading,
                  error,
                }))}
                onAttach={attachFiles}
                mentions={
                  mentionFileId
                    ? {
                        suggestions: fileMentions.suggestions,
                        onPick: fileMentions.onPick,
                        listLabel: t('gov.teamwork.mentions.listLabel'),
                      }
                    : undefined
                }
                acceptFileTypes={CHAT_ATTACHMENT_ACCEPT}
                onRemoveAttachment={(id) =>
                  setPending((prev) => prev.filter((row) => row.id !== id))
                }
                onSend={(body) => {
                  const sent = pending;
                  setPending([]);
                  // Only the ones that finished. A failed upload has no key to
                  // quote, and the composer already refuses to send while any
                  // are still in flight.
                  const attachments = sent
                    .map((row) => row.descriptor)
                    .filter((d): d is NonNullable<typeof d> => Boolean(d));
                  // Returned to the composer, which gives the text back if the
                  // send fails; the attachments come back here for the same reason.
                  return sendMessage
                    .mutateAsync({
                      body,
                      // The same key for every retry of this draft, so a send
                      // that landed but whose response was lost collapses
                      // server-side instead of posting the message twice.
                      clientKey: draftKeys.keyFor(
                        [selected.id, body, ...attachments.map((a) => a.storageKey)].join('\u0000'),
                      ),
                      attachments,
                      ...(mentionFileId
                        ? { mentionUserIds: fileMentions.mentionIdsFor(body) }
                        : {}),
                    })
                    .then((message) => {
                      draftKeys.settle();
                      fileMentions.reset();
                      return message;
                    })
                    .catch((error: unknown) => {
                      snackbar.error(apiErrorMessage(error, t('common.chat.sendFailed')));
                      setPending((prev) => (prev.length > 0 ? prev : sent));
                      throw error;
                    });
                }}
                labels={{
                  placeholder: t('common.chat.composerPlaceholder'),
                  send: t('common.chat.send'),
                  attach: t('common.chat.attach'),
                  removeAttachment: t('common.chat.removeAttachment'),
                  uploading: t('common.chat.uploading'),
                }}
              />
            </>
          ) : (
            <Stack sx={{ flex: 1, alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <Icons.MessageSquare size={iconSize.lg} color={palette.text.muted} aria-hidden />
              <Typography variant="body2" sx={{ mt: 1, color: palette.text.muted }}>
                {t('common.chat.selectPrompt')}
              </Typography>
              {/* One "New conversation", in the page header. A second copy here
                  read as two different actions; the pane points at the one
                  that exists instead. */}
              <Typography
                variant="body2"
                sx={{ mt: 0.5, color: palette.text.muted, maxWidth: 320, textAlign: 'center' }}
              >
                {t('common.chat.selectHint')}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {/* Who is actually in the room. A municipal channel gathers everyone who
          works at that municipality — officers, the clerk, a contractor's
          supervisors — and "9 members" is only useful if you can find out
          whether the person you need is one of them. */}
      <Dialog open={membersOpen} onClose={() => setMembersOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>
          {t('common.chat.memberCount', {
            count: (selected?.participants ?? []).filter((p) => !p.leftAt).length,
          })}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {(selected?.participants ?? [])
              .filter((participant) => !participant.leftAt)
              .sort((a, b) => a.user.displayName.localeCompare(b.user.displayName))
              .map((participant) => (
                <Stack
                  key={participant.user.id}
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: 'center' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {participant.user.displayName}
                      {participant.user.pronouns && (
                        // Beside the name, in the secondary tone: this is how
                        // to address them, not a second name. Rendered only
                        // when they have said — an absent value is not
                        // "unknown", it is nobody's business.
                        <Typography
                          component="span"
                          sx={{ color: 'text.secondary', fontSize: 12.5, ml: 0.75 }}
                        >
                          {participant.user.pronouns}
                        </Typography>
                      )}
                      {participant.user.id === user?.id ? ` — ${t('common.chat.you')}` : ''}
                    </Typography>
                  </Box>
                </Stack>
              ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersOpen(false)}>{t('common.actions.close')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

/**
 * The server resolves a display title already, but the fallbacks it uses for
 * derived threads are English strings baked at write time. Re-deriving the two
 * translatable ones here keeps the French portal French.
 */
/**
 * What a conversation is called on screen.
 *
 * A municipality's standing room stored its title as the bare registry name, so
 * the list read "Moncton · Shediac · Dieppe · General" — four entries of which
 * three looked like places and one like a room, with nothing saying they were
 * all channels. The qualifier is added here rather than stored, so the registry
 * stays the single authority on a municipality's name and the word around it
 * follows the reader's language.
 */
function titleOf(
  conversation: ChatConversationView,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (conversation.kind === 'support') return t('common.chat.supportThread');
  if (conversation.kind === 'file') return t('common.chat.fileThread');
  if (conversation.kind === 'channel' && conversation.localGovernmentId) {
    return t('common.chat.municipalChannel', { name: conversation.title });
  }
  // The one channel with no municipality is the product-wide room every
  // Government Portal account joins (`ChatGeneralChannel` migration). Its stored
  // title is a bare "General", which sat under "General · Miramichi" as if it
  // were a second copy of it. Name its reach the same way the municipal rooms
  // name theirs.
  if (conversation.kind === 'channel') return t('common.chat.sharedChannel');
  return conversation.title;
}

/**
 * Whether one's own message has been read by everyone else still in the thread.
 *
 * Derived from each member's `lastReadAt` rather than stored per message: a
 * member has seen a message exactly when they have read up to at least its
 * timestamp, so that one column answers it for the whole transcript without a
 * row per (message, reader).
 *
 * A thread with nobody else in it reports `sent`. Reporting `read` because the
 * check found no unread member would be a vacuous truth — there is no one to
 * have read it.
 */
function deliveryStatus(
  createdAt: string,
  conversation: ChatConversationView | null,
  viewerId: string | undefined,
): 'sent' | 'read' {
  const others = (conversation?.participants ?? []).filter(
    (participant) => !participant.leftAt && participant.user.id !== viewerId,
  );
  if (others.length === 0) return 'sent';

  const allRead = others.every(
    (participant) => participant.lastReadAt != null && participant.lastReadAt >= createdAt,
  );
  return allRead ? 'read' : 'sent';
}

function isMuted(mutedUntil: string | null): boolean {
  // A timestamp, not a flag — so "muted" is a comparison, and an expired mute
  // needs no job to undo it.
  return Boolean(mutedUntil && new Date(mutedUntil) > new Date());
}

/** Time for today, date otherwise — the list has room for one short stamp. */
function shortStamp(iso: string, locale: string): string {
  const date = new Date(iso);
  const today = new Date();
  const sameDay = date.toDateString() === today.toDateString();
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(
      locale,
      sameDay ? { timeStyle: 'short' } : { month: 'short', day: 'numeric' },
    ).format(date),
  );
}

function fullStamp(iso: string, locale: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    ),
  );
}

/**
 * One line describing a call, in the reader's language.
 *
 * Derived at render rather than stored, for the same reason system messages
 * carry a key: the transcript is bilingual and a duration formatted at write
 * time would be frozen in whichever language the caller happened to be using.
 */
function callSummary(
  call: NonNullable<ChatMessageView['call']>,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (call.status === 'completed') {
    return t('common.chat.call.completed', { duration: formatDuration(call.durationSeconds ?? 0) });
  }
  // An answered call in progress is not "ringing" — the entry updates live, and
  // leaving it on the dial state means a ten-minute conversation reads as an
  // unanswered ring right up until it ends.
  if (call.status === 'active') return t('common.chat.call.active');
  if (call.status === 'missed') return t('common.chat.call.missed');
  if (call.status === 'declined') return t('common.chat.call.declined');
  if (call.status === 'failed') return t('common.chat.call.failed');
  return t('common.chat.call.ringing');
}

function formatDuration(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
